/**
 * HR "Out of System" tab — /hr/leaves (outOfSystemLeave flag).
 *
 * Some leave is hard-blocked at submit (unpaid; annual/birthday/replacement
 * during a blackout). When such leave is genuinely necessary the approval
 * happens OUT OF SYSTEM: the employee talks to their manager, the manager
 * writes a business-continuity plan, and HR approves offline. Once there's a
 * go-signal, HR encodes the outcome here — the leave lands APPROVED, with the
 * business-continuity document + HR justification attached and audited.
 *
 * Backed by POST /api/v1/hr/leaves/encode (+ the existing document upload).
 */
import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Employee, LeaveTypeId } from '@lms/shared-types'

import { encodeOutOfSystemLeave } from '../../api/hr-out-of-system-leave'
import { uploadDocument } from '../../api/documents'
import { ApiError } from '../../api/client'
import { listPublicHolidays } from '../../api/public-holidays'
import { EmployeePickerInput } from '../../components/primitives/EmployeePickerInput'
import { Button } from '../../components/primitives/Button'
import { Card } from '../../components/primitives/Card'
import { FileDropzone } from '../../components/primitives/FileDropzone'
import { useToast } from '../../components/primitives/Toast'
import { HalfDayPicker } from '../../components/employee/HalfDayPicker'
import type { HalfDayMap } from '../../components/employee/HalfDayPicker'
import { LEAVE_TYPE_HALF_DAY_ALLOWED, LEAVE_TYPE_LABEL } from '../../lib/leave-type-meta'
import { ALL_LEAVE_TYPES } from '../../lib/leave-type-palette'
import { isApplicable } from '../../lib/applicable-genders'
import { OosDateRangeField } from './OosDateRangeField'

// Any leave type can be encoded out of system — HR records a leave approved
// offline, whether the submit-time gates would have blocked it (unpaid,
// blackout-period) or not. The picker is gated on employee selection so the
// list can be filtered to that employee's gender-applicable types (only
// maternity/paternity are restricted); the api's is_applicable check is the
// authoritative backstop.
const fieldStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 16,
}

const labelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
}

const hintStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-3)',
}

const controlStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  minHeight: 44,
  boxSizing: 'border-box',
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const details = err.details as { code?: string } | undefined
    if (details?.code === 'no_balance_bucket') {
      return 'This employee has no balance for that leave type and year. Import their balances first.'
    }
    if (details?.code === 'replacement_exceeds_grant') {
      return 'The requested days exceed the employee’s granted replacement leave.'
    }
    return err.message
  }
  return err instanceof Error ? err.message : 'Something went wrong.'
}

export function OutOfSystemTab(): JSX.Element {
  const { notify } = useToast()
  const queryClient = useQueryClient()

  const [employee, setEmployee] = useState<Employee | null>(null)
  const [type, setType] = useState<string>('unpaid')
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const [perDateHalfDays, setPerDateHalfDays] = useState<HalfDayMap>({})
  const [isEmergency, setIsEmergency] = useState<boolean>(false)
  const [reason, setReason] = useState<string>('')
  const [hrReason, setHrReason] = useState<string>('')
  const [docFiles, setDocFiles] = useState<File[]>([])

  const halfDayAllowed =
    LEAVE_TYPE_HALF_DAY_ALLOWED[type as keyof typeof LEAVE_TYPE_HALF_DAY_ALLOWED] ?? false

  const holidaysQuery = useQuery({
    queryKey: ['public-holidays'],
    queryFn: listPublicHolidays,
    staleTime: 60_000,
  })
  const publicHolidayDates = useMemo(
    () => (holidaysQuery.data ?? []).map((h) => h.date),
    [holidaysQuery.data],
  )
  const holidayNames = useMemo(
    () => (holidaysQuery.data ?? []).map((h) => ({ date: h.date, name: h.name })),
    [holidaysQuery.data],
  )

  // Half-day only makes sense for a half-day-allowed type. Switching to a type
  // that disallows it (e.g. Birthday) clears any half selection.
  function onTypeChange(next: string): void {
    setType(next)
    const allowed =
      LEAVE_TYPE_HALF_DAY_ALLOWED[next as keyof typeof LEAVE_TYPE_HALF_DAY_ALLOWED] ?? false
    if (!allowed) setPerDateHalfDays({})
  }

  // The type list depends on the chosen employee: only their gender-applicable
  // types are offered (maternity/paternity are the only gated ones). Empty
  // until an employee is picked, which keeps the dropdown disabled.
  const typeOptions: ReadonlyArray<LeaveTypeId> =
    employee != null
      ? ALL_LEAVE_TYPES.filter((t) => isApplicable(t, employee.gender))
      : []

  // Picking (or switching) the employee can invalidate the current type
  // (e.g. maternity selected, then a male employee chosen) — fall back to the
  // always-applicable Unpaid and drop any half-day selection.
  function onEmployeeChange(next: Employee | null): void {
    setEmployee(next)
    if (next != null && !isApplicable(type as LeaveTypeId, next.gender)) {
      setType('unpaid')
      setPerDateHalfDays({})
    }
  }

  const canSubmit =
    employee != null &&
    startDate != null &&
    endDate != null &&
    reason.trim().length > 0 &&
    hrReason.trim().length > 0 &&
    docFiles.length > 0

  function resetForm(): void {
    setEmployee(null)
    setType('unpaid')
    setStartDate(null)
    setEndDate(null)
    setPerDateHalfDays({})
    setIsEmergency(false)
    setReason('')
    setHrReason('')
    setDocFiles([])
  }

  const encodeMutation = useMutation({
    mutationFn: async () => {
      if (employee == null || startDate == null || endDate == null) {
        throw new Error('Complete all required fields.')
      }
      if (docFiles.length === 0) {
        throw new Error('Attach at least one supporting document.')
      }
      const halfDays = halfDayAllowed
        ? Object.entries(perDateHalfDays)
            .filter(([date]) => date >= startDate && date <= endDate)
            .map(([date, slot]) => ({ date, slot }))
        : []
      const result = await encodeOutOfSystemLeave({
        employeeId: employee.id,
        type,
        startDate,
        endDate,
        halfDays,
        isEmergency,
        reason: reason.trim(),
        outOfSystemReason: hrReason.trim(),
      })
      // Attach the supporting documents to the freshly-created leave, reusing
      // the existing HR-capable upload route. Sequential so a mid-batch failure
      // surfaces cleanly rather than racing.
      for (const file of docFiles) {
        await uploadDocument(result.leaveId, file)
      }
      return result
    },
    onSuccess: (result) => {
      notify(
        `Encoded · ${result.computedDays} day(s) approved for ${employee ? employee.name : 'employee'}`,
        'success',
      )
      resetForm()
      void queryClient.invalidateQueries({ queryKey: ['hr-leaves'] })
    },
    onError: (err) => {
      notify(`Encode failed — ${errorMessage(err)}`, 'error')
    },
  })

  return (
    <div style={{ maxWidth: 720 }} data-testid="oos-encode">
      <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 20, lineHeight: 1.5 }}>
        Encode a leave that was approved out of system — any leave type,
        including ones the submit-time gates would block (unpaid, or a
        blackout-period leave). The leave is recorded as{' '}
        <strong>approved</strong> on the employee’s behalf, with the
        business-continuity document and your justification attached and
        audited.
      </p>

      <Card padding={20} style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={fieldStyle}>
        <label style={labelStyle}>Employee</label>
        <EmployeePickerInput
          value={employee}
          onChange={onEmployeeChange}
          testId="oos-employee"
          placeholder="Search employee by name or email…"
        />
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="oos-type">
          Leave type
        </label>
        <select
          id="oos-type"
          value={type}
          onChange={(e) => onTypeChange(e.target.value)}
          style={controlStyle}
          data-testid="oos-type"
          disabled={employee == null}
        >
          {employee == null ? (
            <option value={type}>—</option>
          ) : (
            typeOptions.map((t) => (
              <option key={t} value={t}>
                {LEAVE_TYPE_LABEL[t]}
              </option>
            ))
          )}
        </select>
        {employee == null ? (
          <span style={hintStyle}>Select an employee first.</span>
        ) : null}
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Dates</label>
        <OosDateRangeField
          startDate={startDate}
          endDate={endDate}
          onChange={(from, to) => {
            setStartDate(from)
            setEndDate(to)
          }}
          publicHolidays={publicHolidayDates}
          holidayNames={holidayNames}
          leaveType={employee != null ? (type as LeaveTypeId) : undefined}
        />
      </div>

      {halfDayAllowed && startDate != null && endDate != null ? (
        <div style={fieldStyle} data-testid="oos-halfdays">
          <label style={labelStyle}>Half days (optional)</label>
          <HalfDayPicker
            startDate={startDate}
            endDate={endDate}
            publicHolidays={publicHolidayDates}
            value={perDateHalfDays}
            onChange={setPerDateHalfDays}
          />
          <span style={hintStyle}>
            Set AM or PM on any day to make it a half day. Each half deducts 0.5
            from the counted days.
          </span>
        </div>
      ) : null}

      <div style={{ ...fieldStyle, flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
        <input
          id="oos-emergency"
          type="checkbox"
          checked={isEmergency}
          onChange={(e) => setIsEmergency(e.target.checked)}
          data-testid="oos-emergency"
          style={{ width: 18, height: 18, marginTop: 2 }}
        />
        <label htmlFor="oos-emergency" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ ...labelStyle, fontWeight: 500 }}>Emergency</span>
          <span style={hintStyle}>
            Mark if this leave was an emergency. Counts toward the employee’s
            leave-pattern metrics as an emergency.
          </span>
        </label>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="oos-reason">
          Leave reason (by the employee)
        </label>
        <textarea
          id="oos-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="e.g. Family emergency; time off needed beyond paid balance."
          style={{ ...controlStyle, minHeight: 60, resize: 'vertical' }}
          data-testid="oos-reason"
        />
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="oos-hr-reason">
          Approval justification (by the manager/HR)
        </label>
        <textarea
          id="oos-hr-reason"
          value={hrReason}
          onChange={(e) => setHrReason(e.target.value)}
          rows={2}
          placeholder="e.g. Manager endorsed; BCP filed; HR approved 2026-09-01."
          style={{ ...controlStyle, minHeight: 60, resize: 'vertical' }}
          data-testid="oos-hr-reason"
        />
        <span style={hintStyle}>Recorded in the audit log as the “why” of this encode.</span>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Upload supporting documents</label>
        <FileDropzone
          files={docFiles}
          onChange={setDocFiles}
          testIdPrefix="oos-bcp"
          hint="At least one document is required (e.g. the business-continuity plan and any approval records). Attached to the encoded leave."
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        <Button
          variant="primary"
          onClick={() => encodeMutation.mutate()}
          disabled={!canSubmit || encodeMutation.isPending}
          data-testid="oos-submit"
        >
          {encodeMutation.isPending ? 'Encoding…' : 'Encode approved leave'}
        </Button>
      </div>
      </Card>
    </div>
  )
}
